import api from './axios';
import { PaginatedResponse, CursorPaginationParams } from '../types';

export interface Language {
  id: string;
  code: string;
  name: string;
  nativeName: string;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    translations: number;
  };
}

export interface Translation {
  id: string;
  key: string;
  value: string;
  languageId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLanguageRequest {
  code: string;
  name: string;
  nativeName: string;
}

export interface UpdateLanguageRequest {
  code?: string;
  name?: string;
  nativeName?: string;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface CreateTranslationRequest {
  key: string;
  value: string;
  languageId: string;
}

export interface UpdateTranslationRequest {
  value: string;
}

export interface BulkTranslationsRequest {
  translations: Record<string, string>;
  languageId: string;
}

export class TranslationsService {
  // Languages API
  async getLanguages(params?: CursorPaginationParams): Promise<PaginatedResponse<Language>> {
    const response = await api.get<Language[]>('/translations/languages', { params });
    // Если API возвращает массив напрямую, преобразуем в нужный формат
    if (Array.isArray(response.data)) {
      return {
        items: response.data,
        total: response.data.length,
        page: 1,
        pages: 1,
        nextCursor: undefined,
        hasNextPage: false
      };
    }
    // Если API возвращает объект с items
    return response.data as PaginatedResponse<Language>;
  }

  async getLanguage(id: string): Promise<Language> {
    const response = await api.get<Language>(`/translations/languages/${id}`);
    return response.data;
  }

  async createLanguage(data: CreateLanguageRequest): Promise<Language> {
    const response = await api.post<Language>('/translations/languages', data);
    return response.data;
  }

  async updateLanguage(id: string, data: UpdateLanguageRequest): Promise<Language> {
    const response = await api.patch<Language>(`/translations/languages/${id}`, data);
    return response.data;
  }

  async deleteLanguage(id: string): Promise<void> {
    await api.delete(`/translations/languages/${id}`);
  }

  // Translations API
  async getTranslations(
    languageId: string, 
    params?: CursorPaginationParams & { search?: string }
  ): Promise<PaginatedResponse<Translation>> {
    const response = await api.get<{
      languageId: string;
      count: number;
      translations: Record<string, string>;
    }>(
      `/translations/languages/${languageId}/translations`, 
      { params }
    );
    
    // Преобразуем объект translations в массив Translation объектов
    const translationsArray: Translation[] = Object.entries(response.data.translations || {}).map(
      ([key, value]) => ({
        id: `${languageId}-${key}`, // Генерируем ID
        key,
        value,
        languageId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
    );

    return {
      items: translationsArray,
      total: translationsArray.length,
      page: 1,
      pages: 1,
      nextCursor: undefined,
      hasNextPage: false
    };
  }

  async createTranslation(data: CreateTranslationRequest): Promise<Translation> {
    const response = await api.post<Translation>(
      `/translations/languages/${data.languageId}/translations`, 
      { key: data.key, value: data.value }
    );
    return response.data;
  }

  async updateTranslation(
    languageId: string, 
    key: string, 
    data: UpdateTranslationRequest
  ): Promise<Translation> {
    try {
      // Попытка обновить существующий перевод
      const response = await api.patch<Translation>(
        `/translations/languages/${languageId}/translations/${encodeURIComponent(key)}`, 
        data
      );
      return response.data;
    } catch (error: unknown) {
      // Если перевод не найден (404), создаем новый
      if (error && typeof error === 'object' && 'response' in error && (error as { response?: { status?: number } }).response?.status === 404) {
        console.log(`Translation '${key}' not found, creating new one`);
        return this.createTranslation({
          key,
          value: data.value,
          languageId
        });
      }
      throw error;
    }
  }

  async deleteTranslation(languageId: string, key: string): Promise<void> {
    console.log('Delete translation:', { languageId, key });
    const url = `/translations/languages/${languageId}/translations/${encodeURIComponent(key)}`;
    console.log('Delete URL:', url);
    await api.delete(url);
  }

  // Bulk operations
  async bulkImportTranslations(data: BulkTranslationsRequest): Promise<{ imported: number }> {
    const response = await api.post<{ imported: number }>(
      `/translations/languages/${data.languageId}/import`, 
      { translations: data.translations }
    );
    return response.data;
  }

  async exportTranslations(languageId: string): Promise<Record<string, string>> {
    const response = await api.get<Record<string, string>>(
      `/translations/languages/${languageId}/export`
    );
    return response.data;
  }

  async clearProjectLanguageTranslations(projectId: string, languageId: string): Promise<void> {
    await api.delete(`/translations/projects/${projectId}/languages/${languageId}/clear-all-translations`);
  }
}

export const translationsService = new TranslationsService();
